import { CalendarCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DarkPill, IconTile, OutlinePill, calendarDialogClass, calendarOverlayClass } from "@/components/calendar/ui";
import { formatMonthDay, upcomingEvents, type StepSchedule } from "@/lib/calendar";
import { cn } from "@/lib/utils";

/**
 * Ticking off a step that's a routine with dates still ahead: the person decides whether
 * those dates stay on their calendar. Closing without choosing leaves the step open.
 * Without a working connection the dates can't be removed, so only keeping is offered.
 */
export function RoutineDoneDialog({
  stepText,
  schedule,
  canRemove,
  onKeep,
  onRemove,
  onCancel,
}: {
  stepText: string | null;
  schedule: StepSchedule | undefined;
  /** False when Google Calendar isn't connected (or the connection ended). */
  canRemove: boolean;
  onKeep: () => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const upcoming = schedule ? upcomingEvents(schedule) : [];
  const last = [...upcoming].sort((a, b) => b.start.localeCompare(a.start))[0];
  const n = upcoming.length;

  return (
    <Dialog open={!!stepText} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent hideClose overlayClassName={calendarOverlayClass} className={cn(calendarDialogClass, "sm:max-w-[460px]")}>
        <div className="px-6 pb-7 pt-7 sm:px-8 sm:pt-8">
          <IconTile tone="green" size="lg" className="rounded-full">
            <CalendarCheck className="h-6 w-6" />
          </IconTile>
          <DialogTitle className="mt-5 font-display text-[24px] font-semibold tracking-[-0.02em] text-foreground">
            Nice work. And the rest of the routine?
          </DialogTitle>
          <DialogDescription className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            “{stepText}” still has {n} {n === 1 ? "date" : "dates"} on your calendar
            {last ? `, through ${formatMonthDay(new Date(last.start))}` : ""}. Dates that already happened stay either way.
          </DialogDescription>
          <div className="mt-6 flex flex-col gap-3">
            <DarkPill size="lg" onClick={onKeep}>
              Keep the remaining {n} {n === 1 ? "date" : "dates"}
            </DarkPill>
            {canRemove ? (
              <OutlinePill size="lg" onClick={onRemove}>
                Remove them from my calendar
              </OutlinePill>
            ) : (
              <p className="text-center text-[13.5px] leading-relaxed text-muted-foreground">
                Reconnect Google Calendar in Settings to remove them.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
